import { Box, HStack, VStack, Text, Menu, useDisclosure, Portal } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom'
import { FiUser, FiLogOut, FiUsers, FiGrid, FiMail, FiSettings, FiKey } from 'react-icons/fi'
import { useAuth } from '../../../hooks/useAuth'
import { useProfile } from '../../../hooks/useProfile'
import { signOutOrganizer } from '../../../lib/session/organizerSession'
import { UserAvatar } from '../../admin/UserAvatar'
import { ProfileEditModal } from '../../profile/ProfileEditModal'
import { CommitteeSwitcher } from '../CommitteeSwitcher'
import { resolveVisibleModule } from '../../../modules/registry'
import { useProductTier } from '../../../hooks/useClientConfig'
import type { ProductTier } from '../../../types/config'

function pageTitle(pathname: string, tier: ProductTier): string {
  const mod = resolveVisibleModule(pathname, tier)
  if (mod) return mod.label
  if (pathname === '/dashboard') return 'Dashboard'
  if (pathname.startsWith('/dashboard/contacts')) return 'Contacts'
  if (pathname.startsWith('/dashboard/lists') || pathname.startsWith('/dashboard/searches')) return 'Lists'
  if (pathname.startsWith('/dashboard/admin/users')) return 'Users'
  if (pathname.startsWith('/dashboard/admin/grants')) return 'Access Grants'
  if (pathname.startsWith('/dashboard/admin/invites')) return 'Invites'
  if (pathname.startsWith('/dashboard/admin/committees')) return 'Committees'
  return 'ORCL'
}

export function MobileTopBar({ pathname }: { pathname: string }) {
  const navigate = useNavigate()
  const { isAdmin } = useAuth()
  const { data: profile } = useProfile()
  const tier = useProductTier()
  const profileModal = useDisclosure()

  const handleLogout = () => void signOutOrganizer()

  return (
    <Box
      bg="surface"
      borderBottom="1px"
      borderColor="gray.200"
      px={4}
      py={3}
      position="sticky"
      top={0}
      zIndex={10}
    >
      <HStack justify="space-between">
        <HStack gap={2}>
          <Box
            w={7} h={7} bg="accent" borderRadius="md"
            display="flex" alignItems="center" justifyContent="center" flexShrink={0}
          >
            <Text color="accent.fg" fontWeight="bold" fontSize="sm">BT</Text>
          </Box>
          <VStack align="start" gap={0}>
            <Text fontWeight="bold" fontSize="md" lineHeight="short" lineClamp={1}>
              {pageTitle(pathname, tier)}
            </Text>
          </VStack>
        </HStack>

        <Menu.Root positioning={{
          placement: 'bottom-end'
        }}>
          <Menu.Trigger
            minW="44px"
            minH="44px"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <UserAvatar
              userId={profile?.id ?? ''}
              firstName={profile?.firstName ?? ''}
              portraitUrl={profile?.portraitUrl}
              size="sm"
            />
          </Menu.Trigger>
          <Portal><Menu.Positioner><Menu.Content>
                <Menu.Item onSelect={profileModal.onOpen} value='item-0'><FiUser />Profile</Menu.Item>
                {isAdmin && (
                  <>
                    <Menu.Separator />
                    <Menu.Item
                      onSelect={() => navigate('/dashboard/admin/users')}
                      value='item-1'><FiUsers />Users</Menu.Item>
                    <Menu.Item
                      onSelect={() => navigate('/dashboard/admin/grants')}
                      value='item-1b'><FiKey />Access Grants</Menu.Item>
                    <Menu.Item
                      onSelect={() => navigate('/dashboard/admin/invites')}
                      value='item-2'><FiMail />Invites</Menu.Item>
                    <Menu.Item
                      onSelect={() => navigate('/dashboard/admin/committees')}
                      value='item-3'><FiGrid />Committees</Menu.Item>
                  </>
                )}
                <Menu.Separator />
                <Menu.Item onSelect={() => navigate('/dashboard/settings')} value='settings'><FiSettings />Settings</Menu.Item>
                <Menu.Separator />
                <Menu.Item onSelect={handleLogout} value='item-4'><FiLogOut />Logout</Menu.Item>
              </Menu.Content></Menu.Positioner></Portal>
        </Menu.Root>
      </HStack>

      {/* WL-6: the "Viewing:" committee switcher lives in the nav chrome under the ORCL banner
          (self-hides when the caller has no committees to switch to). */}
      <CommitteeSwitcher />

      <ProfileEditModal isOpen={profileModal.open} onClose={profileModal.onClose} />
    </Box>
  );
}
